import type { ArchivedGameStats } from "../domain/baseballStats";
import type {
  ActiveGameViewModel,
  GameModel,
  PlayerListItem,
  PlayoffSeriesModel,
  PregameViewModel,
  ScheduleModel,
  SeasonModel,
} from "../shared/types/contracts";
import type { EnqueueCommand, RunCommand } from "./commandTypes";
import type { OptimisticGameController } from "./useOptimisticGame";
import type { OptimisticPregameController } from "./useOptimisticPregame";
import type { OptimisticPlayerPreferencesController } from "./useOptimisticPlayerPreferences";
import type { OptimisticScheduleController } from "./useOptimisticSchedule";
import type { PlayDraftController } from "./usePlayDraft";
import { useGameCommands } from "./useGameCommands";
import { useInviteCommands } from "./useInviteCommands";
import { usePregameCommands } from "./usePregameCommands";
import { useScheduleCommands } from "./useScheduleCommands";
import { useSeasonCommands } from "./useSeasonCommands";
import type { TeamCommands } from "./teamTypes";

interface TeamCommandOptions {
  teamId: string;
  players: PlayerListItem[];
  prefs: Record<string, string[]>;
  activePlayers: PlayerListItem[];
  pregame: PregameViewModel;
  game: ActiveGameViewModel | null;
  gameArchive: ArchivedGameStats[];
  scheduleGames: ScheduleModel[];
  playoffSeries: PlayoffSeriesModel[];
  seasons: SeasonModel[];
  activeSeasonId: string | null;
  rawGame: GameModel | null | undefined;
  playDraft: PlayDraftController;
  run: RunCommand;
  enqueue: EnqueueCommand;
  projectGame: OptimisticGameController["project"];
  projectUndo: OptimisticGameController["projectUndo"];
  markGameCommitted: OptimisticGameController["markCommitted"];
  rejectGameProjection: OptimisticGameController["reject"];
  projectPregame: OptimisticPregameController["project"];
  markPregameCommitted: OptimisticPregameController["markCommitted"];
  rejectPregameProjection: OptimisticPregameController["reject"];
  projectPreference: OptimisticPlayerPreferencesController["projectToggle"];
  preferenceRevision: OptimisticPlayerPreferencesController["currentRevision"];
  markPreferenceCommitted: OptimisticPlayerPreferencesController["markCommitted"];
  rejectPreferenceProjection: OptimisticPlayerPreferencesController["reject"];
  projectSchedule: OptimisticScheduleController["projectUpdate"];
  markScheduleCommitted: OptimisticScheduleController["markCommitted"];
  rejectScheduleProjection: OptimisticScheduleController["reject"];
  schedulePregameBarrier: OptimisticScheduleController["pregameBarrier"];
  showToast: (message: string) => void;
  scoringBlocked: boolean;
  scorerName: string | null;
  onScorerClaimed: (name: string | null) => void;
  onScheduleSelected: (scheduleId: string, committedRevision: number) => void;
}

export function useTeamCommands({
  teamId,
  players,
  prefs,
  activePlayers,
  pregame,
  game,
  gameArchive,
  scheduleGames,
  playoffSeries,
  seasons,
  activeSeasonId,
  rawGame,
  playDraft,
  run,
  enqueue,
  projectGame,
  projectUndo,
  markGameCommitted,
  rejectGameProjection,
  projectPregame,
  markPregameCommitted,
  rejectPregameProjection,
  projectPreference,
  preferenceRevision,
  markPreferenceCommitted,
  rejectPreferenceProjection,
  projectSchedule,
  markScheduleCommitted,
  rejectScheduleProjection,
  schedulePregameBarrier,
  showToast,
  scoringBlocked,
  scorerName,
  onScorerClaimed,
  onScheduleSelected,
}: TeamCommandOptions): TeamCommands {
  const scheduleCommands = useScheduleCommands({
    teamId,
    scheduleGames,
    playoffSeries,
    run,
    enqueue,
    onScheduleSelected,
    projectUpdate: projectSchedule,
    markCommitted: markScheduleCommitted,
    rejectProjection: rejectScheduleProjection,
  });
  const { gameAction, ...gameCommands } = useGameCommands({
    teamId,
    game,
    rawGame,
    playDraft,
    run,
    enqueue,
    project: projectGame,
    projectUndo,
    markCommitted: markGameCommitted,
    rejectProjection: rejectGameProjection,
    showToast,
    scoringBlocked,
    scorerName,
    onScorerClaimed,
  });
  const pregameCommands = usePregameCommands({
    teamId,
    players,
    prefs,
    activePlayers,
    pregame,
    game,
    gameArchive,
    scheduleGames,
    activeSeasonId,
    run,
    enqueue,
    gameAction,
    updateSchedule: scheduleCommands.updateSchedule,
    project: projectPregame,
    markCommitted: markPregameCommitted,
    rejectProjection: rejectPregameProjection,
    projectPreference,
    preferenceRevision,
    markPreferenceCommitted,
    rejectPreferenceProjection,
    startBarrier: schedulePregameBarrier,
    showToast,
  });
  const inviteCommands = useInviteCommands({ teamId, run, enqueue });
  const seasonCommands = useSeasonCommands({
    teamId,
    seasons,
    activeSeasonId,
    run,
    enqueue,
    showToast,
  });

  return {
    ...pregameCommands,
    ...gameCommands,
    ...inviteCommands,
    ...scheduleCommands,
    ...seasonCommands,
  };
}
